import { Button, IconNode, Modal, Typography } from '@components/base';
import css from './index.module.scss';
import Images from '@assets/images';
import { useEffect, useMemo, useState } from 'react';
import { fetchAllUsers } from '@services/user.service';
import notify from '@helpers/toastify-helper';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { SelectLabel } from '@components/common';
import { PeopleType, POC_TYPE_MAPPING } from '@helpers/constants';
import { addEditPOC } from '@services/order.service';
import { IPeople, IUSERTYPES } from '@helpers/types/people';

interface AddPOCModalProps {
  open: boolean;
  onClose: () => void;
  pocData: IPeople | null;
  onAddPocFormSubmit: () => void;
}

interface IForm {
  field_poc: {
    poc: IUSERTYPES | null;
  }[];
}

const AddPOCModal = (props: AddPOCModalProps) => {
  const { open, onClose, pocData, onAddPocFormSubmit } = props;

  const [modalState, setModalState] = useState<{
    userList: IUSERTYPES[];
    isLoading: boolean;
  }>({
    userList: [],
    isLoading: false
  });

  const orderPocId = useMemo(() => {
    return pocData?.order_poc_id || '';
  }, [pocData]);

  const { userList, isLoading } = modalState;

  const getUserList = async () => {
    setModalState((prevState) => ({ ...prevState, isLoading: true }));
    const response = await fetchAllUsers({ sort_by_groups: 'True', role: 'internal' });
    setModalState((prevState) => ({ ...prevState, isLoading: false }));
    if (response.success) {
      const { data } = response;
      const pocType = pocData?.poc_type as keyof typeof POC_TYPE_MAPPING;
      const pocTypeKey = pocType ? POC_TYPE_MAPPING[pocType] : '';
      const users = data[pocTypeKey] || [];
      setModalState((prevState) => ({ ...prevState, userList: users }));
    } else if (response.error) notify({ message: response.error, severity: 'error' });
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<IForm>({
    defaultValues: {
      field_poc: [
        {
          poc: null
        }
      ]
    }
  });

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    control,
    name: 'field_poc'
  });

  useEffect(() => {
    getUserList();
  }, []);

  const handleFormSubmit = async (data: IForm) => {
    const userId = data.field_poc
      .map((item: { poc: IUSERTYPES | null }) => item.poc?.user_id)
      .filter((item: string | undefined) => item != null) as string[];

    const reqBody = {
      user_ids: userId
    };

    const response = await addEditPOC(orderPocId, reqBody);
    if (response.success) {
      notify({
        message: 'POC added successfully',
        severity: 'success',
        dismissible: true
      });
      reset();
      onClose();
      onAddPocFormSubmit();
    } else {
      notify({
        message: response?.error ?? 'Unable to add POC',
        severity: 'error',
        dismissible: true
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={css.modalWrapper}>
        <section className={css.modalHeader}>
          <Typography variant="h4">
            Add {pocData?.internal_team} {PeopleType(pocData?.poc_type || '')}
          </Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)} className={css.formWrapper}>
          <section className={css.modalContent}>
            {fields.map((item, index) => {
              return (
                <div key={item.id} className={css.fieldWrapper}>
                  <div className={css.blockWrapper}>
                    <Controller
                      name={`field_poc.${index}.poc`}
                      control={control}
                      key={item.id}
                      render={({ field, fieldState }) => (
                        <SelectLabel
                          {...field}
                          required
                          isLoading={isLoading}
                          label="Select POC"
                          isSearchable
                          options={userList}
                          defaultValue={null}
                          getOptionLabel={(option: any) =>
                            `${option.given_name} ${option.family_name}`
                          }
                          getOptionValue={(option: any) => option.user_id}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                    <div>
                      {fields.length > 1 ? (
                        <IconNode
                          src={Images.deleteRed}
                          className={css.pocDeleteIcon}
                          onClick={() => remove(index)}
                          style={{ cursor: 'pointer' }}
                          alt="delete icon"
                        />
                      ) : (
                        <IconNode
                          className={css.pocDeleteIcon}
                          src={Images.delete}
                          alt="delete icon"
                          title="Disabled"
                        />
                      )}
                    </div>
                  </div>
                  <div
                    className={css.addPocText}
                    onClick={() => {
                      append({ poc: null });
                    }}
                  >
                    Add POC
                  </div>
                </div>
              );
            })}
          </section>
          <section className={css.modalFooter}>
            <Button variant="outlined-secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              Save
            </Button>
          </section>
        </form>
      </div>
    </Modal>
  );
};

export default AddPOCModal;
